// Pure shaping of the agent calendar payload (docs/real-estate/be/
// 10-api-reference.md §A11) into FullCalendar event descriptors. Kept free
// of Vuex/component state — colour, dark-mode styling and selection are
// layered on afterwards in AgentCalendar.vue — so every layer/filter
// combination can be covered here with plain vitest.
//
// `days` has one entry per (date, assigned project); `layer` names which
// rule decided it:
//   specific            Layer 1 one-off assignment           source_type "specific_assignment"
//   normal              Layer 3 regular schedule              source_type "schedule"
//   property_hours      Layer 0 fallback (no L1/L3 entry)      source_type "operating_hours"
//   unavailable         Layer 2 all-day leave, time_slots []   source_type "unavailability"
//   unavailable_partial Layer 2 partial leave, already netted  source_type "unavailability"
//   property_closed     Layer 0 closed that date, time_slots []
//
// For `unavailable_partial`, `time_slots` are the hours the agent STILL
// WORKS (leave already subtracted) — rendered as working-hours events with
// a leave marker, not as leave itself. `property_closed` renders nothing
// here; the day cell is greyed by the calendar component separately.
const LEAVE_LAYERS = ['unavailable', 'unavailable_partial'];

// Short labels for event titles ("Viewing · Aisyah Rahman", "Ali · On
// leave"), distinct from constants.js's APPT_TYPES (too long for a calendar
// chip). Components pass translated ones in (REAL_ESTATE.CALENDAR.EVENT_*);
// these English defaults keep the function usable without vue-i18n.
const DEFAULT_LABELS = {
  viewing: 'Viewing',
  key_handover: 'Key handover',
  onLeave: 'On leave',
};

function matchesKeyword(agentName, keyword) {
  if (!keyword) return true;
  return agentName.toLowerCase().includes(keyword.trim().toLowerCase());
}

function includeAgent(agent, filters) {
  if (filters.agentId && agent.agent_id !== filters.agentId) return false;
  if (!matchesKeyword(agent.agent_name, filters.keyword)) return false;
  return true;
}

function includeDay(day, filters) {
  if (filters.propertyId && day.property_id !== filters.propertyId) {
    return false;
  }
  return true;
}

// One event per (agent, project, date, time_slot) — a split shift (two
// time_slots on the same day, e.g. the Layer-0 operating-hours example in
// §A11) becomes two separate events, never one spanning both. The id must
// include the project: an agent assigned to two projects gets one `days`
// entry per project for the same date, and FullCalendar drops events that
// share an id.
function workingHoursEvents(agent, day) {
  return day.time_slots.map((slot, index) => ({
    id: `wh-${agent.agent_id}-${day.property_id}-${day.date}-${index}`,
    title: `${agent.agent_name} · ${day.property_name} ${slot.start_time}–${slot.end_time}`,
    start: `${day.date}T${slot.start_time}`,
    end: `${day.date}T${slot.end_time}`,
    allDay: false,
    // `re-cal-working-hours` is the generic hook AgentCalendar.vue styles
    // for every working-hours block; `re-cal-leave-partial` layers the
    // italic/hatched leave marker on top for unavailable_partial only.
    classNames:
      day.layer === 'unavailable_partial'
        ? ['re-cal-working-hours', 're-cal-leave-partial']
        : ['re-cal-working-hours'],
    extendedProps: {
      kind: 'working_hours',
      layer: day.layer,
      source_type: day.source_type,
      source_id: day.source_id,
      agent_id: agent.agent_id,
      agent_name: agent.agent_name,
      property_id: day.property_id,
      property_name: day.property_name,
      date: day.date,
      start_time: slot.start_time,
      end_time: slot.end_time,
      assignment_date_range: day.assignment_date_range || null,
      isLeavePartial: day.layer === 'unavailable_partial',
    },
  }));
}

// All-day leave — `time_slots` is empty, so there's nothing to split into
// per-slot events; one event covers the whole day.
function allDayLeaveEvent(agent, day, labels) {
  return {
    id: `leave-${agent.agent_id}-${day.date}-${day.source_id}`,
    title: `${agent.agent_name} · ${labels.onLeave}`,
    start: day.date,
    allDay: true,
    classNames: ['re-cal-leave-allday'],
    extendedProps: {
      kind: 'leave_all_day',
      layer: day.layer,
      source_type: day.source_type,
      source_id: day.source_id,
      agent_id: agent.agent_id,
      agent_name: agent.agent_name,
      property_id: day.property_id,
      property_name: day.property_name,
      date: day.date,
    },
  };
}

function isAllDayLeave(day) {
  return LEAVE_LAYERS.includes(day.layer) && day.time_slots.length === 0;
}

function dayToEvents(agent, day, labels) {
  if (day.layer === 'property_closed') return [];
  if (isAllDayLeave(day)) return [allDayLeaveEvent(agent, day, labels)];
  return workingHoursEvents(agent, day);
}

function bookingEvent(appointment, agentName, labels) {
  const typeLabel =
    labels[appointment.appointment_type] || appointment.appointment_type;
  // Times go in the title (same as working hours) because the calendar
  // turns off FullCalendar's own "9a"-style time label.
  const times = appointment.end_time
    ? `${appointment.start_time}–${appointment.end_time}`
    : appointment.start_time;
  return {
    id: `appt-${appointment.appointment_id}`,
    title: `${typeLabel} · ${appointment.customer_name} ${times}`,
    start: `${appointment.date}T${appointment.start_time}`,
    end: appointment.end_time
      ? `${appointment.date}T${appointment.end_time}`
      : undefined,
    allDay: false,
    classNames: ['re-cal-booking', `re-cal-booking-${appointment.status}`],
    extendedProps: {
      kind: 'booking',
      appointment_id: appointment.appointment_id,
      agent_id: appointment.agent_id,
      agent_name: agentName,
      property_id: appointment.property_id,
      property_name: appointment.property_name,
      appointment_type: appointment.appointment_type,
      status: appointment.status,
      date: appointment.date,
      start_time: appointment.start_time,
      end_time: appointment.end_time,
      customer_id: appointment.customer_id,
      customer_name: appointment.customer_name,
    },
  };
}

// `calendarData`: the raw `{ agents, appointments }` response body (from_date
// /to_date aren't needed here). `filters`: { agentId?, propertyId?, keyword? }
// — keyword matches the agent's name, same field the left panel searches.
// `labels`: optional translated { viewing, key_handover, onLeave }.
export function transformToEvents(calendarData, filters = {}, labels = {}) {
  const titleLabels = { ...DEFAULT_LABELS, ...labels };
  const agents = (calendarData && calendarData.agents) || [];
  const appointments = (calendarData && calendarData.appointments) || [];

  const events = [];
  // All-projects leave comes back once per assigned project (same
  // source_id each time) — show it once per agent and date, not once per
  // project.
  const seenLeave = new Set();

  agents.forEach(agent => {
    if (!includeAgent(agent, filters)) return;
    agent.days.forEach(day => {
      if (!includeDay(day, filters)) return;
      if (isAllDayLeave(day)) {
        const key = `${agent.agent_id}|${day.date}|${day.source_id}`;
        if (seenLeave.has(key)) return;
        seenLeave.add(key);
      }
      events.push(...dayToEvents(agent, day, titleLabels));
    });
  });

  const agentNameById = new Map(agents.map(a => [a.agent_id, a.agent_name]));

  appointments.forEach(appointment => {
    // Defensive — the BE never returns cancelled bookings (§A11), but a
    // calendar event for one would be actively misleading if it ever did.
    if (appointment.status === 'cancelled') return;
    if (filters.propertyId && appointment.property_id !== filters.propertyId) {
      return;
    }
    if (filters.agentId && appointment.agent_id !== filters.agentId) return;
    const agentName = agentNameById.get(appointment.agent_id) || '';
    if (filters.keyword && !matchesKeyword(agentName, filters.keyword)) {
      return;
    }
    events.push(bookingEvent(appointment, agentName, titleLabels));
  });

  return events;
}
